import React, { useState, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDate } from '../../utils/dateUtils';
import '../shared.css';
import './Modal.css';
import './KGColumnModal.css';

interface CreateKGModalProps {
  onClose: () => void;
  onCreated: () => void;
}

interface SelectedFile {
  name: string;
  s3_key: string;
}

interface FileItem {
  id: string;
  name: string;
  type: string;
  size?: number;
  modified_date?: string;
  s3_path?: string;
  parent_id: string;
}

const WS_PAGE = 20;
const FILE_PAGE = 20;

const getFileIcon = (type: string, name: string): string => {
  if (type === 'folder') return '📁';
  if (name.toLowerCase().endsWith('.csv')) return '📄';
  if (type === 'analysis_v2') return '📊';
  if (type === 'notebook') return '📓';
  return '📄';
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isFileSelectable = (file: FileItem): boolean =>
  file.type !== 'folder' && file.name.toLowerCase().endsWith('.csv');

const CreateKGModal: React.FC<CreateKGModalProps> = ({ onClose, onCreated }) => {
  // ── form ────────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataVersionChips, setDataVersionChips] = useState<string[]>([]);
  const [dataVersionInput, setDataVersionInput] = useState('');
  const dataVersionInputRef = useRef<HTMLInputElement>(null);
  const [nodeFiles, setNodeFiles] = useState<SelectedFile[]>([]);
  const [edgeFiles, setEdgeFiles] = useState<SelectedFile[]>([]);
  const [gdsEnabled, setGdsEnabled] = useState(false);
  const [gdsError, setGdsError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── column state ────────────────────────────────────────────────────────
  const [activeCol, setActiveCol] = useState<'form' | 'workspaces' | 'files'>('form');
  const [currentFileType, setCurrentFileType] = useState<'node' | 'edge' | null>(null);

  // ── workspaces ──────────────────────────────────────────────────────────
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [wsLoading, setWsLoading] = useState(false);
  const [wsError, setWsError] = useState('');
  const [wsSearch, setWsSearch] = useState('');
  const [wsSearchInput, setWsSearchInput] = useState('');
  const [wsFrom, setWsFrom] = useState(0);
  const [wsTotal, setWsTotal] = useState(0);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);

  // ── files ───────────────────────────────────────────────────────────────
  const [files, setFiles] = useState<FileItem[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileFrom, setFileFrom] = useState(0);
  const [fileTotal, setFileTotal] = useState(0);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; name: string }[]>([]);
  const [currentParentId, setCurrentParentId] = useState('');
  const [pendingFiles, setPendingFiles] = useState<FileItem[]>([]);
  const [selectingAll, setSelectingAll] = useState(false);

  // ── load workspaces ─────────────────────────────────────────────────────
  const loadWorkspaces = useCallback(async (search: string, from: number) => {
    setWsLoading(true);
    setWsError('');
    try {
      const response: any = await apiService.searchWorkspaces('{user_id}', from, WS_PAGE, search);
      if (response.hits) {
        const list = response.hits.hits.map((hit: any) => ({
          id: hit._id,
          name: hit._source.name || hit._source.workspace_name || 'Unnamed Workspace',
          created_date: hit._source.created_date,
          description: hit._source.description,
          owner: hit._source.owner || hit._source.created_by,
        }));
        setWorkspaces(list);
        setWsTotal(response.hits.total?.value ?? response.hits.total ?? 0);
        setWsFrom(from);
      }
    } catch (err: any) {
      setWsError(extractErrorMessage(err, 'Failed to load workspaces'));
    } finally {
      setWsLoading(false);
    }
  }, []);

  // ── load files ──────────────────────────────────────────────────────────
  const loadFiles = useCallback(async (wsId: string, parentId: string, from: number) => {
    setFileLoading(true);
    setFileError('');
    try {
      const response: any = await apiService.listWorkspaceFiles(
        wsId,
        parentId,
        '{document_index}',
        '{workspace_index}',
        from,
        FILE_PAGE
      );
      if (response.hits) {
        const list: FileItem[] = response.hits.hits.map((hit: any) => ({
          id: hit._id,
          name: hit._source.entity_name || hit._source.name || 'Unnamed',
          type: hit._source.entity_type || 'file',
          size: hit._source.size,
          modified_date: hit._source.modified_date || hit._source.updated_date,
          s3_path: hit._source.s3_key || hit._source.s3_path,
          parent_id: hit._source.parent_id || parentId,
        }));
        setFiles(list);
        setFileTotal(response.hits.total?.value ?? response.hits.total ?? 0);
        setFileFrom(from);
      }
    } catch (err: any) {
      setFileError(extractErrorMessage(err, 'Failed to load files'));
    } finally {
      setFileLoading(false);
    }
  }, []);

  // ── browse button ───────────────────────────────────────────────────────
  const handleBrowse = useCallback((type: 'node' | 'edge') => {
    setCurrentFileType(type);
    setWsSearch('');
    setWsSearchInput('');
    setSelectedWorkspace(null);
    setFiles([]);
    setPendingFiles([]);
    setBreadcrumb([]);
    setCurrentParentId('');
    setActiveCol('workspaces');
    loadWorkspaces('', 0);
  }, [loadWorkspaces]);

  // ── workspace click ─────────────────────────────────────────────────────
  const handleWorkspaceClick = useCallback((ws: any) => {
    setSelectedWorkspace(ws);
    setBreadcrumb([{ id: ws.id, name: ws.name }]);
    setCurrentParentId(ws.id);
    setPendingFiles([]);
    setFiles([]);
    setActiveCol('files');
    loadFiles(ws.id, ws.id, 0);
  }, [loadFiles]);

  // ── confirm files ───────────────────────────────────────────────────────
  const handleConfirmFiles = useCallback(() => {
    const newFiles: SelectedFile[] = pendingFiles
      .filter((f) => f.s3_path)
      .map((f) => ({ name: f.name, s3_key: f.s3_path! }));

    const mergeUnique = (existing: SelectedFile[]): SelectedFile[] => {
      const updated = [...existing];
      newFiles.forEach((f) => {
        if (!updated.some((e) => e.s3_key === f.s3_key)) updated.push(f);
      });
      return updated;
    };

    if (currentFileType === 'node') setNodeFiles((prev) => mergeUnique(prev));
    else if (currentFileType === 'edge') setEdgeFiles((prev) => mergeUnique(prev));

    // reset column state
    setPendingFiles([]);
    setFiles([]);
    setWorkspaces([]);
    setSelectedWorkspace(null);
    setBreadcrumb([]);
    setCurrentParentId('');
    setWsSearch('');
    setWsSearchInput('');
    setCurrentFileType(null);
    setActiveCol('form');
  }, [pendingFiles, currentFileType]);

  // ── cancel browse ───────────────────────────────────────────────────────
  const handleCancelBrowse = useCallback(() => {
    setPendingFiles([]);
    setFiles([]);
    setWorkspaces([]);
    setSelectedWorkspace(null);
    setBreadcrumb([]);
    setCurrentParentId('');
    setWsSearch('');
    setWsSearchInput('');
    setCurrentFileType(null);
    setActiveCol('form');
  }, []);

  // ── workspace search ────────────────────────────────────────────────────
  const handleWsSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setWsSearch(wsSearchInput);
    loadWorkspaces(wsSearchInput, 0);
  };

  // ── folder navigation ───────────────────────────────────────────────────
  const handleFolderClick = useCallback((folder: FileItem) => {
    if (!selectedWorkspace) return;
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentParentId(folder.id);
    setPendingFiles([]);
    loadFiles(selectedWorkspace.id, folder.id, 0);
  }, [selectedWorkspace, loadFiles]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (!selectedWorkspace) return;
    setBreadcrumb((prev) => {
      const next = prev.slice(0, index + 1);
      const targetId = next[next.length - 1].id;
      setCurrentParentId(targetId);
      setPendingFiles([]);
      loadFiles(selectedWorkspace.id, targetId, 0);
      return next;
    });
  }, [selectedWorkspace, loadFiles]);

  // ── checkbox toggle ─────────────────────────────────────────────────────
  const handleToggleFile = useCallback((file: FileItem) => {
    if (!isFileSelectable(file)) return;
    setPendingFiles((prev) => {
      const exists = prev.some((f) => f.id === file.id);
      return exists ? prev.filter((f) => f.id !== file.id) : [...prev, file];
    });
  }, []);

  // ── select all ──────────────────────────────────────────────────────────
  const handleSelectAll = useCallback(async () => {
    if (!selectedWorkspace) return;
    setSelectingAll(true);
    setFileError('');
    try {
      const totalPages = Math.ceil(fileTotal / FILE_PAGE);
      const all: FileItem[] = [];
      for (let i = 0; i < totalPages; i++) {
        const response: any = await apiService.listWorkspaceFiles(
          selectedWorkspace.id,
          currentParentId,
          '{document_index}',
          '{workspace_index}',
          i * FILE_PAGE,
          FILE_PAGE
        );
        if (response.hits) {
          response.hits.hits.forEach((hit: any) => {
            all.push({
              id: hit._id,
              name: hit._source.entity_name || hit._source.name || 'Unnamed',
              type: hit._source.entity_type || 'file',
              size: hit._source.size,
              modified_date: hit._source.modified_date || hit._source.updated_date,
              s3_path: hit._source.s3_key || hit._source.s3_path,
              parent_id: hit._source.parent_id || currentParentId,
            });
          });
        }
      }
      const selectable = all.filter(isFileSelectable);
      setPendingFiles((prev) => {
        const ids = new Set(prev.map((f) => f.id));
        return [...prev, ...selectable.filter((f) => !ids.has(f.id))];
      });
    } catch (err: any) {
      setFileError(extractErrorMessage(err, 'Failed to select all files'));
    } finally {
      setSelectingAll(false);
    }
  }, [selectedWorkspace, currentParentId, fileTotal]);

  const handleUnselectAll = useCallback(() => {
    setPendingFiles([]);
  }, []);

  // ── data version chip helpers ────────────────────────────────────────────
  const commitDataVersionChip = useCallback(() => {
    const val = dataVersionInput.trim().replace(/,+$/, '').trim();
    if (val && !dataVersionChips.includes(val)) {
      setDataVersionChips((prev) => [...prev, val]);
    }
    setDataVersionInput('');
  }, [dataVersionInput, dataVersionChips]);

  const handleDataVersionKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      commitDataVersionChip();
    } else if (e.key === 'Backspace' && dataVersionInput === '') {
      setDataVersionChips((prev) => prev.slice(0, -1));
    }
  }, [commitDataVersionChip, dataVersionInput]);

  const removeDataVersionChip = useCallback((chip: string) => {
    setDataVersionChips((prev) => prev.filter((c) => c !== chip));
    dataVersionInputRef.current?.focus();
  }, []);

  // ── show toast (replaces inline error banner) ───────────────────────────
  const showToast = useCallback((msg: string, title = 'Error') => {
    setGdsError(`__TITLE__${title}__MSG__${msg}`);
  }, []);

  // ── submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setGdsError('');
    if (!name.trim()) { showToast('KG name is required', 'Validation Error'); return; }
    if (!description.trim()) { showToast('Description is required', 'Validation Error'); return; }
    const pendingChip = dataVersionInput.trim().replace(/,+$/, '').trim();
    const allChips = pendingChip && !dataVersionChips.includes(pendingChip)
      ? [...dataVersionChips, pendingChip]
      : dataVersionChips;
    if (allChips.length === 0) { showToast('Data version is required', 'Validation Error'); return; }
    if (nodeFiles.length === 0) { showToast('At least one node file is required', 'Validation Error'); return; }
    try {
      setLoading(true);
      await apiService.createKG({
        kg_name: name.trim(),
        kg_description: description.trim(),
        data_version: allChips.join(', '),
        data_files: {
          nodes: nodeFiles.map((f) => f.s3_key),
          edges: edgeFiles.map((f) => f.s3_key),
        },
        gds_enabled: gdsEnabled,
      });
      onCreated();
    } catch (err: any) {
      const msg = extractErrorMessage(err, 'Failed to create knowledge graph');
      if (gdsEnabled) {
        setGdsEnabled(false);
        showToast(msg || 'GDS is not available for your account. Please contact your administrator.', 'GDS Access Unavailable');
      } else {
        showToast(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── overlay click — only close when on form col ─────────────────────────
  const handleOverlayClick = () => {
    if (activeCol === 'form') onClose();
  };

  const toastTitle = gdsError.startsWith('__TITLE__')
    ? gdsError.split('__MSG__')[0].replace('__TITLE__', '')
    : 'Error';
  const toastMsg = gdsError.startsWith('__TITLE__')
    ? gdsError.split('__MSG__')[1] ?? ''
    : gdsError;

  return (
    <>
    {gdsError && (
      <div className="kcm-gds-toast">
        <div className="kcm-gds-toast-hd">
          <span className="kcm-gds-toast-icon">⚠</span>
          <span className="kcm-gds-toast-title">{toastTitle}</span>
          <button className="kcm-gds-toast-close" onClick={() => setGdsError('')}>×</button>
        </div>
        <div className="kcm-gds-toast-body">{toastMsg}</div>
      </div>
    )}
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`kcm-modal kcm-modal--${activeCol}`} onClick={(e) => e.stopPropagation()}>

        {/* ── Left column: form ─────────────────────────────────────────── */}
        <div className="kcm-col kcm-col-form">
          <div className="kcm-col-hd">
            <span className="kcm-col-title">New Knowledge Graph</span>
          </div>

          <div className="kcm-col-body">
            {/* KG Name */}
            <div className="kcm-field">
              <label className="kcm-label">
                KG Name <span className="kcm-required">*</span>
              </label>
              <input
                className="kcm-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. EchoMap, BaseKG"
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="kcm-field">
              <label className="kcm-label">
                Description <span className="kcm-required">*</span>
              </label>
              <input
                className="kcm-input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description"
              />
            </div>

            {/* Data Version */}
            <div className="kcm-field">
              <label className="kcm-label">
                Data Version <span className="kcm-required">*</span>
              </label>
              <div
                className="kcm-chip-input"
                onClick={() => dataVersionInputRef.current?.focus()}
              >
                {dataVersionChips.map((chip) => (
                  <span key={chip} className="kcm-chip">
                    {chip}
                    <button
                      type="button"
                      className="kcm-chip-rm"
                      onClick={(e) => { e.stopPropagation(); removeDataVersionChip(chip); }}
                      tabIndex={-1}
                    >×</button>
                  </span>
                ))}
                <input
                  ref={dataVersionInputRef}
                  className="kcm-chip-text"
                  type="text"
                  value={dataVersionInput}
                  onChange={(e) => setDataVersionInput(e.target.value)}
                  onKeyDown={handleDataVersionKeyDown}
                  onBlur={commitDataVersionChip}
                  placeholder={dataVersionChips.length === 0 ? 'e.g. v1.0, 2024-Q3 — press Enter to add' : ''}
                />
              </div>
            </div>

            {/* Node Files bucket */}
            <div className="kcm-bucket">
              <div className="kcm-bucket-hd">
                <span className="kcm-bucket-label">
                  Node Files <span className="kcm-required">*</span>
                </span>
                <div className="kcm-bucket-hd-right">
                  {nodeFiles.length > 0 && (
                    <span className="kcm-bucket-count">
                      {nodeFiles.length} file{nodeFiles.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {nodeFiles.length > 0 && (
                    <button className="kcm-bucket-clear" onClick={() => setNodeFiles([])}>
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              <div className="kcm-bucket-body">
                {nodeFiles.length === 0 ? (
                  <div className="kcm-bucket-empty">No files selected yet</div>
                ) : (
                  <ul className="kcm-bucket-list">
                    {nodeFiles.map((f, i) => (
                      <li key={f.s3_key} className="kcm-bucket-item">
                        <span className="kcm-bucket-name" title={f.s3_key}>📄 {f.name}</span>
                        <button
                          className="kcm-bucket-rm"
                          onClick={() => setNodeFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          title="Remove"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                className={`kcm-bucket-browse${activeCol !== 'form' && currentFileType === 'node' ? ' kcm-bucket-browse--active' : ''}`}
                onClick={() => handleBrowse('node')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                {nodeFiles.length === 0 ? 'Browse Node Files' : '+ Add More'}
              </button>
            </div>

            {/* Edge Files bucket */}
            <div className="kcm-bucket">
              <div className="kcm-bucket-hd">
                <span className="kcm-bucket-label">Edge Files</span>
                <div className="kcm-bucket-hd-right">
                  {edgeFiles.length > 0 && (
                    <span className="kcm-bucket-count">
                      {edgeFiles.length} file{edgeFiles.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {edgeFiles.length > 0 && (
                    <button className="kcm-bucket-clear" onClick={() => setEdgeFiles([])}>
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              <div className="kcm-bucket-body">
                {edgeFiles.length === 0 ? (
                  <div className="kcm-bucket-empty">No files selected yet</div>
                ) : (
                  <ul className="kcm-bucket-list">
                    {edgeFiles.map((f, i) => (
                      <li key={f.s3_key} className="kcm-bucket-item">
                        <span className="kcm-bucket-name" title={f.s3_key}>📄 {f.name}</span>
                        <button
                          className="kcm-bucket-rm"
                          onClick={() => setEdgeFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          title="Remove"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                className={`kcm-bucket-browse${activeCol !== 'form' && currentFileType === 'edge' ? ' kcm-bucket-browse--active' : ''}`}
                onClick={() => handleBrowse('edge')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                {edgeFiles.length === 0 ? 'Browse Edge Files' : '+ Add More'}
              </button>
            </div>

            {/* GDS toggle */}
            <div className="kcm-field kcm-field--toggle">
              <label className="kcm-toggle-row" htmlFor="gds-toggle">
                <div className="kcm-toggle-info">
                  <span className="kcm-label">Enable Graph Data Science (GDS)</span>
                  <span className="kcm-toggle-hint">
                    Enables Neo4j GDS algorithms (PageRank, community detection, etc.).
                    Requires GDS access on your account.
                  </span>
                </div>
                <button
                  id="gds-toggle"
                  type="button"
                  role="switch"
                  aria-checked={gdsEnabled}
                  className={`kcm-switch${gdsEnabled ? ' kcm-switch--on' : ''}`}
                  onClick={() => setGdsEnabled((v) => !v)}
                >
                  <span className="kcm-switch-thumb" />
                </button>
              </label>
            </div>

          </div>

          <div className="kcm-col-ft">
            <button className="tab-btn tab-btn--ghost" onClick={onClose}>Cancel</button>
            <button
              className="tab-btn tab-btn--primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <><span className="inline-spinner" /> Creating…</>
              ) : 'Create KG'}
            </button>
          </div>
        </div>

        {/* ── Middle column: workspaces ──────────────────────────────────── */}
        {activeCol !== 'form' && (
          <div className="kcm-col kcm-col-workspaces">
            <div className="kcm-col-hd">
              <span className="kcm-col-title">Select Workspace</span>
              <button
                className="tab-btn tab-btn--ghost kcm-col-close"
                onClick={handleCancelBrowse}
              >✕</button>
            </div>

            <div className="kcm-col-body">
              {/* Search */}
              <form className="kcm-ws-search" onSubmit={handleWsSearch}>
                <input
                  className="tab-search"
                  style={{ flex: 1, width: 'auto' }}
                  type="text"
                  value={wsSearchInput}
                  onChange={(e) => setWsSearchInput(e.target.value)}
                  placeholder="Search workspaces…"
                />
                <button type="submit" className="tab-btn tab-btn--ghost" disabled={wsLoading}>
                  Search
                </button>
                {wsSearch && (
                  <button
                    type="button"
                    className="tab-btn tab-btn--ghost"
                    onClick={() => { setWsSearchInput(''); setWsSearch(''); loadWorkspaces('', 0); }}
                  >✕</button>
                )}
              </form>

              {wsError && <div className="kcm-col-error">{wsError}</div>}

              {wsLoading ? (
                <div className="kcm-col-loader">
                  <span className="tab-spinner" />
                </div>
              ) : workspaces.length === 0 ? (
                <div className="tab-empty" style={{ padding: '24px 0' }}>
                  <div className="tab-empty-icon">🗂</div>
                  <div className="tab-empty-body">No workspaces found</div>
                </div>
              ) : (
                <div className="kcm-ws-list">
                  {workspaces.map((ws) => (
                    <div
                      key={ws.id}
                      className={`kcm-ws-row${selectedWorkspace?.id === ws.id ? ' kcm-ws-row--active' : ''}`}
                      onClick={() => handleWorkspaceClick(ws)}
                    >
                      <span className="kcm-ws-icon">🗂</span>
                      <div className="kcm-ws-info">
                        <div className="kcm-ws-name">{ws.name}</div>
                        <div className="kcm-ws-meta">
                          {ws.owner ? `${ws.owner} · ` : ''}{formatDate(ws.created_date)}
                        </div>
                      </div>
                      <span className="kcm-ws-chevron">›</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {wsTotal > WS_PAGE && !wsLoading && (
                <div className="kcm-pagination">
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={() => loadWorkspaces(wsSearch, Math.max(0, wsFrom - WS_PAGE))}
                    disabled={wsFrom === 0}
                  >‹ Prev</button>
                  <span className="kcm-pagination-info">
                    {wsFrom + 1}–{Math.min(wsFrom + WS_PAGE, wsTotal)} of {wsTotal}
                  </span>
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={() => loadWorkspaces(wsSearch, wsFrom + WS_PAGE)}
                    disabled={wsFrom + WS_PAGE >= wsTotal}
                  >Next ›</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Right column: files ────────────────────────────────────────── */}
        {activeCol === 'files' && (
          <div className="kcm-col kcm-col-files">
            <div className="kcm-col-hd">
              {/* Breadcrumb */}
              <div className="kcm-breadcrumb">
                {breadcrumb.map((crumb, idx) => (
                  <React.Fragment key={crumb.id}>
                    {idx < breadcrumb.length - 1 ? (
                      <span
                        className="kcm-bc-item"
                        onClick={() => handleBreadcrumbClick(idx)}
                      >{crumb.name}</span>
                    ) : (
                      <span className="kcm-bc-current">{crumb.name}</span>
                    )}
                    {idx < breadcrumb.length - 1 && (
                      <span className="kcm-bc-sep">›</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Select / Unselect all */}
              {fileTotal > 0 && !fileLoading && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={handleSelectAll}
                    disabled={selectingAll || fileLoading}
                  >
                    {selectingAll ? '…' : 'All'}
                  </button>
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={handleUnselectAll}
                    disabled={pendingFiles.length === 0}
                  >None</button>
                </div>
              )}
            </div>

            <div className="kcm-col-body">
              {fileError && <div className="kcm-col-error">{fileError}</div>}

              {fileLoading ? (
                <div className="kcm-col-loader">
                  <span className="tab-spinner" />
                </div>
              ) : files.length === 0 ? (
                <div className="tab-empty" style={{ padding: '24px 0' }}>
                  <div className="tab-empty-icon">📂</div>
                  <div className="tab-empty-body">This folder is empty</div>
                </div>
              ) : (
                <div className="kcm-files-list">
                  {files.map((file) => {
                    const selectable = isFileSelectable(file);
                    const isFolder = file.type === 'folder';
                    const isSelected = pendingFiles.some((f) => f.id === file.id);
                    return (
                      <div
                        key={file.id}
                        className={[
                          'kcm-file-row',
                          isSelected ? 'kcm-file-row--selected' : '',
                          !selectable && !isFolder ? 'kcm-file-row--disabled' : '',
                          isFolder ? 'kcm-file-row--folder' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => {
                          if (isFolder) handleFolderClick(file);
                          else if (selectable) handleToggleFile(file);
                        }}
                      >
                        {selectable && (
                          <input
                            type="checkbox"
                            className="kcm-file-cb"
                            checked={isSelected}
                            onChange={() => handleToggleFile(file)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {!selectable && !isFolder && (
                          <span style={{ width: 14, flexShrink: 0 }} />
                        )}
                        <span className="kcm-file-icon">
                          {getFileIcon(file.type, file.name)}
                        </span>
                        <span className="kcm-file-name" title={file.name}>
                          {file.name}
                        </span>
                        {file.size != null && (
                          <span className="kcm-file-meta">{formatFileSize(file.size)}</span>
                        )}
                        {isFolder && <span className="kcm-file-chevron">›</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* File pagination */}
              {fileTotal > FILE_PAGE && !fileLoading && selectedWorkspace && (
                <div className="kcm-pagination">
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={() => loadFiles(selectedWorkspace.id, currentParentId, Math.max(0, fileFrom - FILE_PAGE))}
                    disabled={fileFrom === 0}
                  >‹ Prev</button>
                  <span className="kcm-pagination-info">
                    {fileFrom + 1}–{Math.min(fileFrom + FILE_PAGE, fileTotal)} of {fileTotal}
                  </span>
                  <button
                    className="tab-btn tab-btn--ghost"
                    style={{ height: '26px', padding: '0 8px', fontSize: '11px' }}
                    onClick={() => loadFiles(selectedWorkspace.id, currentParentId, fileFrom + FILE_PAGE)}
                    disabled={fileFrom + FILE_PAGE >= fileTotal}
                  >Next ›</button>
                </div>
              )}
            </div>

            <div className="kcm-col-ft">
              <span className="kcm-confirm-info">
                {pendingFiles.length > 0
                  ? `${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''} selected`
                  : 'Select CSV files'}
              </span>
              <button
                className="tab-btn tab-btn--primary"
                onClick={handleConfirmFiles}
                disabled={pendingFiles.length === 0}
              >
                Add {pendingFiles.length > 0 ? `${pendingFiles.length} ` : ''}File{pendingFiles.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
};

export default CreateKGModal;
